import {
    checkValidRelativeDateString,
    parseJSONHandlebarsTemplate,
    parsePlainTextToHandlebarsTemplate,
} from "@regal-voice/shared-parsers";

import { isStringValidRegalDateFormat } from "@regal-voice/ui";
import { isValidMp3File } from "Components/FormItems/shared";
import { isValidLink } from "Services/HelpersService";

import type { RuleValidationFn } from "../types";
import type { RcFile } from "antd/es/upload";
import type { UploadFile } from "antd/es/upload/interface";
import type { Rule } from "antd/lib/form";
import type { ZodType } from "zod";

type GenericInputRule = {
    required?: boolean;
    whitespace?: boolean;
    message: string;
};

export const generateFormItemRequiredRules = (inputRequired: string, noMessage = false, assertWhiteSpace = true) => {
    const rules: GenericInputRule[] = [
        {
            required: true,
            message: noMessage ? "" : `${inputRequired} is required.`,
        },
    ];

    if (assertWhiteSpace) {
        rules.push({
            whitespace: true,
            message: noMessage ? "" : `${inputRequired} cannot be blank spaces only.`,
        });
    }

    return rules;
};

export const generateFormItemDefinedRule = (inputRequired: string) => ({
    async validator(_: unknown, value: unknown) {
        if (value === undefined) {
            return Promise.reject(`${inputRequired} is required.`);
        }
        return Promise.resolve();
    },
});

export const generateFormItemStartsWithRule = (prefix: string, label = "This field") => ({
    pattern: new RegExp(`^${prefix}`),
    message: `${label} must start with ${prefix}`,
});

export const generateFormItemMaxLengthRule = (maxLength: number, label = "This field") => ({
    max: maxLength,
    message: `${label} cannot be longer than ${maxLength} characters.`,
});

export const KEBAB_CASE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const generateKebabCaseSlugRule = (label = "This field"): Rule => ({
    pattern: KEBAB_CASE_SLUG_PATTERN,
    message: `${label} must use lowercase letters, numbers, and hyphens only (e.g. my-function).`,
});

// TODO: conlidate with OR define usecase for this vs generatePhoneNumberValidator
export const generateFormItemPhoneNumberRule = (label = "Phone Number") => {
    return {
        async validator(_: unknown, value: string) {
            if (!/^1?\d{10}$/.test(value?.replace(/[^\d]/g, "") || "")) {
                throw `${label} must be valid 10 digit number.`;
            }
        },
    };
};

// TODO: conlidate with OR define usecase for this vs generateFormItemPhoneNumberRules
export const generatePhoneNumberValidator = (ErrorDisplay: JSX.Element) => {
    return {
        async validator(_: any, value: string) {
            if (!value || value.match(/^\+/)) {
                return Promise.resolve();
            }
            throw ErrorDisplay;
        },
    };
};

export const generateExistanceValidator = (message: string) => {
    return {
        async validator(_: any, value?: string) {
            if (value === undefined || value === null || value === "") {
                return Promise.reject(message);
            }
            return Promise.resolve();
        },
    };
};

export const generateNumberExistenceValidator = (message: string, min?: number, max?: number): Rule => {
    return {
        async validator(_: unknown, value?: string) {
            if (isNaN(Number(value))) {
                return Promise.reject(message);
            }
            if (min && Number(value) < min) {
                return Promise.reject(message);
            }
            if (max && Number(value) > max) {
                return Promise.reject(message);
            }
            return Promise.resolve();
        },
    };
};

export function generateContainsWhitespaceValidator(message: string): RuleValidationFn {
    return (rule: unknown, value?: string) => {
        return value?.includes(" ") ? Promise.reject(message) : Promise.resolve();
    };
}

/** Response path: only allow letters, numbers, dots, underscores, and hyphens */
export function generateResponsePathValidator(message?: string): Rule {
    const err = message ?? "Only letters, numbers, dots, underscores, and hyphens are allowed.";
    return {
        validator(_: unknown, value?: string) {
            if (value == null || value === "") {
                return Promise.resolve();
            }
            if (!/^[a-zA-Z0-9._\-]+$/.test(value)) {
                return Promise.reject(err);
            }
            return Promise.resolve();
        },
    };
}

export function generateDisallowSelectionValidator(disallow: Array<string>, message: string): RuleValidationFn {
    return (rule: unknown, value: string) => {
        const disallowMessage = `${value} ${message}`;
        return disallow.includes(value) ? Promise.reject(disallowMessage) : Promise.resolve();
    };
}

// IF you are using this. you should update your form to use a Form.List be using the isDuplicateListValue from ListValidators
export function generateDuplicateOptionValidator(message: string, options: Array<string>): RuleValidationFn {
    return (rule: unknown, value: string) => {
        const isDupe = value !== "" && options.includes(value);
        return isDupe ? Promise.reject(message) : Promise.resolve();
    };
}

export function generateSchemaPropertyNameRule(existingNames: string[]): Rule {
    return {
        validator(_: unknown, value?: string) {
            const name = value?.trim() ?? "";
            if (!name) {
                return Promise.reject(new Error("A field name is required."));
            }
            if (existingNames.some((existingName) => existingName.trim() === name)) {
                return Promise.reject(new Error("A field with this name already exists."));
            }
            return Promise.resolve();
        },
    };
}

export function generateDisallowedListValidator(disallowedList: Array<string>, message: string): RuleValidationFn {
    return (_: any, value: string) => {
        const isDisallowed = disallowedList.includes(value);
        if (isDisallowed) {
            return Promise.reject(message);
        }
        return Promise.resolve();
    };
}

export function generateDateTimeValidator(message: string) {
    return {
        validator(_: unknown, value: string) {
            if (isStringValidRegalDateFormat(value)) {
                return Promise.resolve();
            }
            return Promise.reject(message);
        },
    };
}

export function generateHandlebarsTemplateValidator(
    message: string = "Invalid template formatting.",
    allowEmpty = true
) {
    return {
        validator: (_rule: unknown, value: string) => {
            try {
                if (allowEmpty && !value) {
                    return Promise.resolve();
                }
                const parts = parsePlainTextToHandlebarsTemplate(value);
                if (parts?.status == "error") {
                    return Promise.reject(message);
                }
                return Promise.resolve();
            } catch {
                return Promise.reject(message);
            }
        },
    };
}

export function generateJsonValidator(message: string, allowEmpty = false) {
    return (_rule: unknown, value: string) => {
        try {
            if (allowEmpty && !value) {
                return Promise.resolve();
            }
            const parts = parseJSONHandlebarsTemplate(value);
            if (parts?.status == "error") {
                return Promise.reject(message);
            }
            return Promise.resolve();
        } catch {
            return Promise.reject(message);
        }
    };
}

export function generateUrlValidator(message: string) {
    return (rule: unknown, value: string) => {
        try {
            new URL(value);
        } catch {
            return Promise.reject(message);
        }
        return Promise.resolve();
    };
}

async function audioSelectionAndDurationValidation(maxDurationSeconds: number, fileObj?: RcFile) {
    if (!fileObj) {
        return Promise.reject(`Please select an audio file.`);
    }
    const audioCtx = new window.AudioContext();
    try {
        if (!(await isValidMp3File(fileObj))) {
            return Promise.reject(`Audio file format cannot be accepted. See above link for more guidance.`);
        }
        const buffer = await audioCtx.decodeAudioData(await fileObj.arrayBuffer());
        if (buffer.duration > maxDurationSeconds) {
            return Promise.reject(`Please select an audio file under ${maxDurationSeconds} seconds long.`);
        }
        return Promise.resolve();
    } finally {
        try {
            await audioCtx.close();
        } catch {
            // Preserve the original validation result if AudioContext cleanup fails.
        }
    }
}

export function generateAudionSelectionAndDurationValidator(duration: number) {
    return {
        validator(_: unknown, value: UploadFile<any>) {
            return audioSelectionAndDurationValidation(duration, value?.originFileObj);
        },
    };
}

// Alternative custom validator for URLs. Seems to work better than the one above which allows some weird URLs.
export function generateLinkValidator(message: string) {
    return {
        validator(_: unknown, value: string) {
            if (!value || isValidLink(value)) {
                return Promise.resolve();
            } else {
                return Promise.reject(message);
            }
        },
    };
}

function isValidEndpoint(endpoint: string): boolean {
    try {
        const url = new URL(endpoint);
        // Ensure protocol is http or https
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

export function generateEndpointValidator(message?: string) {
    return {
        validator(_: unknown, value: string) {
            if (!value || isValidEndpoint(value)) {
                return Promise.resolve();
            } else {
                return Promise.reject(message || "Endpoint must be a valid URL.");
            }
        },
    };
}

export const disallowDecimalInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ".") {
        event.preventDefault();
    }
};

export function generateIntegerValidator() {
    return {
        validator(_: unknown, value: string | number) {
            if (Number.isInteger(Number(value))) {
                return Promise.resolve();
            }
            return Promise.reject("No decimals allowed.");
        },
    };
}

export function generateBooleanTextValidator(message = 'Enter "true" or "false".') {
    return {
        async validator(_: unknown, value: unknown) {
            if (value === "true" || value === "false") {
                return;
            }
            return Promise.reject(message);
        },
    };
}

export function generateMaxValueValidator(fieldName: string, max: number, errorMessage?: string) {
    errorMessage = errorMessage || `${fieldName} cannot exceed ${max}.`;
    return {
        validator(_: unknown, value: number) {
            if (value > max) {
                return Promise.reject(errorMessage);
            }
            return Promise.resolve();
        },
    };
}

export function generateMinValueValidator(
    fieldName: string,
    min: number,
    errorMessage?: string,
    onlyFailIfValueIsNumber = false
) {
    errorMessage = errorMessage || `${fieldName} cannot be less than ${min}.`;
    return {
        validator(_: unknown, value: number) {
            if (value < min) {
                if (onlyFailIfValueIsNumber && typeof value !== "number") {
                    return Promise.resolve();
                }
                return Promise.reject(errorMessage);
            }
            return Promise.resolve();
        },
    };
}

export function generateRangeValidator(min: number, max: number, unit: string = "") {
    const unitText = unit ? ` ${unit}` : "";
    return {
        validator(_: unknown, value: number | string) {
            const numValue = typeof value === "string" ? parseFloat(value) : value;

            if (value === null || value === undefined || value === "" || isNaN(numValue)) {
                return Promise.reject(`Value is required.`);
            }
            if (numValue < min || numValue > max) {
                return Promise.reject(`Value must be between ${min} and ${max}${unitText}.`);
            }
            return Promise.resolve();
        },
    };
}

const DTMF_KEY_VALID_PATTERN = /^[0-9*#A-D]$/;
const DTMF_KEY_DEFAULT_MESSAGE = "Must be a valid DTMF key (0-9, *, #, A-D)";

export function generateDtmfKeyValidator(message?: string): Rule {
    return {
        validator(_: unknown, value: string) {
            if (!value || DTMF_KEY_VALID_PATTERN.test(value)) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(message ?? DTMF_KEY_DEFAULT_MESSAGE));
        },
    };
}

export function generateNonEmptyListValidator(error?: string) {
    return generateMinimumListSizeValidator(1, error);
}

export function generateMinimumListSizeValidator(minimum: number, error?: string) {
    return {
        validator: (_: any, values: Array<string> | undefined) => {
            if (!values || values.length < minimum) {
                return Promise.reject(
                    error ?? `Please select at least ${minimum > 1 ? minimum : "one"} value${minimum > 1 ? "s" : ""}.`
                );
            }
            return Promise.resolve();
        },
    };
}

export function generateNoDoubleQuotesValidator(error?: string) {
    return {
        validator: (_: unknown, values: Array<string> | undefined) => {
            if (Array.isArray(values) && values.some((v) => typeof v === "string" && v.includes('"'))) {
                return Promise.reject(error ?? "Values must not contain double quote characters.");
            }
            return Promise.resolve();
        },
    };
}

export function generateActionNameValidator(message?: string) {
    const regex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    return {
        validator(_: unknown, value: string) {
            if (!value) {
                return Promise.reject(message ?? "Name is required.");
            }

            if (!regex.test(value)) {
                return Promise.reject(
                    message ??
                        "Name must start with a letter and can only contain letters, numbers, underscores, and no spaces."
                );
            }
            return Promise.resolve();
        },
    };
}

export function generateAttributeNameValidator(message?: string) {
    const regex = /^[a-zA-Z0-9_]+$/;
    return {
        validator(_: unknown, value: string) {
            if (!value) {
                return Promise.resolve();
            }
            if (!regex.test(value)) {
                return Promise.reject(message ?? "Only letters, numbers, and underscores are allowed.");
            }
            return Promise.resolve();
        },
    };
}

export function generateRelativeDateValidator(message?: string) {
    return {
        validator(_: any, relativeDateValue: string) {
            if (relativeDateValue && checkValidRelativeDateString(relativeDateValue)) {
                return Promise.resolve();
            }
            return Promise.reject(message ?? "Invalid relative date format");
        },
    };
}

/**
 * Wraps any Zod schema as an AntD Form Rule so validation copy and logic
 * stay in sync with the backend automatically.
 *
 * Values are passed through unchanged. String schemas should declare a
 * `required_error` message on z.string() so that untouched (undefined)
 * fields surface the same copy as empty-string fields.
 */
export function generateZodValidationRule<T>(schema: ZodType<T>): Rule {
    return {
        validator: async (_, value) => {
            const result = schema.safeParse(value);
            if (!result.success) {
                throw new Error(result.error.issues[0]?.message ?? "Invalid value");
            }
        },
    };
}

/**
 * Same as generateZodValidationRule, but skips validation for empty/undefined values — for fields
 * that are conditionally required elsewhere and shouldn't fail format validation when blank.
 */
export function generateOptionalZodValidationRule<T>(schema: ZodType<T>): Rule {
    return {
        validator: async (_, value) => {
            if (!value) {
                return;
            }
            const result = schema.safeParse(value);
            if (!result.success) {
                throw new Error(result.error.issues[0]?.message ?? "Invalid value");
            }
        },
    };
}

export const generateMinimumSelectionValidator = (message: string, min = 1): Rule => ({
    type: "array",
    min,
    message,
});

export const generateOptionalIntegerRangeValidator = (message: string, min: number, max: number): Rule => ({
    async validator(_: unknown, value?: string) {
        if (value === undefined || value === null || value === "") {
            return Promise.resolve();
        }
        const numeric = Number(value);
        if (!Number.isInteger(numeric) || numeric < min || numeric > max) {
            return Promise.reject(new Error(message));
        }
        return Promise.resolve();
    },
});
